import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRecoEngine } from "../context/RecoEngineContext";
import { postRecommendations } from "../api/recommendations";
import useRecoEngineSession from "./useRecoEngineSession";
import { handleRenderedBlocks } from "../services";

interface QueryObject {
  query?: string;
  [key: string]: any;
}

interface ErrorBlock {
  type: "chat";
  data: {
    text: string;
    engineResponse: boolean;
  };
}

const ERROR_MESSAGE = "Sorry, I encountered an error while processing your request.";

const createErrorBlock = (): ErrorBlock => ({
  type: "chat",
  data: {
    text: ERROR_MESSAGE,
    engineResponse: true
  }
});

/**
 * Handles the API call and response processing
 */
const processRecommendations = async (queryObject: QueryObject, t: any) => {
  const payload = {
    ...queryObject,
    query: queryObject.query || "",
  };

  const response = await postRecommendations(payload);
  const { sessionBlocks, uiBlocks } = handleRenderedBlocks(response, t);

  return {
    response,
    payload,
    sessionBlocks,
    uiBlocks
  };
};

/**
 * Updates the session with new blocks
 */
const updateSessionBlocks = (sessionBlocks: any[], addBlock: (block: any) => void) => {
  sessionBlocks.forEach(block => {
    addBlock(block);
  });
};

/**
 * Updates the UI with new blocks
 */
const updateUIBlocks = (
  uiBlocks: any[], 
  setRenderBlocks: (fn: (prev: any[]) => any[]) => void,
  setFeedbackRow: (value: boolean) => void
) => {
  setRenderBlocks((prevBlocks) => [...prevBlocks, ...uiBlocks]);
  setFeedbackRow(true);
};

/**
 * Handles errors during recommendation fetching
 */
const handleRecommendationError = (err: any, addBlock: (block: any) => void) => {
  console.error("Error fetching recommendations", err);
  addBlock(createErrorBlock());
};

const useRecommendationApi = () => {
  const {
    setLoading,
    setQueryObject,
    setServicesObject,
    setRenderBlocks,
    setFeedbackRow,
  } = useRecoEngine();

  const {
    addBlock,
    updateSessionData,
    setRefineFormData
  } = useRecoEngineSession();

  const [t] = useTranslation("translations");

  const fetchRecommendations = useCallback(async (queryObject: QueryObject) => {
    setLoading(true);
    try {
      // Process recommendations and get blocks
      const { response, payload, sessionBlocks, uiBlocks } = 
        await processRecommendations(queryObject, t);
      
      // Update session and state
      updateSessionData({ servicesObject: response });
      setQueryObject(payload);
      setServicesObject(response);

      // Update blocks
      updateSessionBlocks(sessionBlocks, addBlock);
      updateUIBlocks(uiBlocks, setRenderBlocks, setFeedbackRow);
      
      // Reset refine form
      setRefineFormData({ isActive: false });
    } catch (err) {
      handleRecommendationError(err, addBlock);
        } finally {
          setLoading(false);
        }
    }, [
      setLoading,
      updateSessionData,
      setQueryObject,
      setServicesObject,
      t,
      addBlock,
      setRenderBlocks,
      setFeedbackRow,
      setRefineFormData
    ]);
    
    return { fetchRecommendations };
  };

export default useRecommendationApi;